import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import UploadZone from "@/components/UploadZone";
import { toast } from "sonner";
import { FileText, Mic, Globe, Type, Camera, ArrowRight, Loader2, Zap, X } from "lucide-react";
import { useSubscription } from "@/contexts/SubscriptionContext";

/** Success toast + navigation. Handles the multi-topic split case. */
function announceAndNavigate(nav, note) {
  const siblings = note?.siblings || [];
  if (siblings.length > 0) {
    toast.success(
      `Split into ${siblings.length + 1} topic notes — 1 credit used`,
      { description: "We detected multiple topics and gave each its own note." }
    );
  } else {
    toast.success("Notes ready");
  }
  nav(`/app/note/${note.id}`);
}

const TABS = [
  { id: "text", label: "Text", icon: Type },
  { id: "pdf", label: "PDF", icon: FileText },
  { id: "url", label: "URL", icon: Globe },
  { id: "audio", label: "Audio / Video", icon: Mic },
  { id: "photos", label: "Photos", icon: Camera },
];

const ACCEPTED_PHOTO_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_PHOTOS = 10;
const MAX_PHOTO_MB = 8;

function PhotosPicker({ files, setFiles }) {
  const inputRef = React.useRef(null);
  const cameraRef = React.useRef(null);
  const [previews, setPreviews] = React.useState([]);

  React.useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const validated = [];
    for (const f of incoming) {
      if (!ACCEPTED_PHOTO_MIME.includes(f.type)) {
        toast.error(`${f.name}: only JPEG / PNG / WEBP supported`);
        continue;
      }
      if (f.size > MAX_PHOTO_MB * 1024 * 1024) {
        toast.error(`${f.name}: too large (max ${MAX_PHOTO_MB} MB)`);
        continue;
      }
      validated.push(f);
    }
    const combined = [...files, ...validated].slice(0, MAX_PHOTOS);
    if (files.length + validated.length > MAX_PHOTOS) {
      toast.warning(`Max ${MAX_PHOTOS} photos — extras were ignored`);
    }
    setFiles(combined);
  };

  const removeAt = (idx) => setFiles(files.filter((_, i) => i !== idx));

  return (
    <div data-testid="photos-drop" className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="photos-upload-btn"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-3 bg-white border-2 border-black btn-brutal"
        >
          <FileText className="w-3.5 h-3.5" /> Choose from library
        </button>
        <button
          type="button"
          data-testid="photos-camera-btn"
          onClick={() => cameraRef.current?.click()}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-3 bg-white border-2 border-black btn-brutal"
        >
          <Camera className="w-3.5 h-3.5" /> Take photo
        </button>
        <span className="ml-auto self-center font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          {files.length} / {MAX_PHOTOS} · JPEG · PNG · WEBP · ≤ {MAX_PHOTO_MB} MB each
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />

      {previews.length === 0 ? (
        <div
          className="border-2 border-dashed border-black/30 p-10 text-center cursor-pointer hover:bg-white/60"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        >
          <Camera className="w-8 h-8 mx-auto text-zinc-400" />
          <p className="mt-3 font-heading font-bold">Drop or pick 1–{MAX_PHOTOS} photos</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Whiteboard · handwritten notes · textbook pages · slides
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {previews.map((src, i) => (
            <div key={i} data-testid={`photo-thumb-${i}`} className="relative group border-2 border-black">
              <img src={src} alt="" className="w-full aspect-square object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 w-6 h-6 bg-black text-white flex items-center justify-center hover:bg-red-600"
                aria-label="Remove photo"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <span className="absolute bottom-0 left-0 px-1.5 py-0.5 bg-black text-white font-mono text-[9px]">
                {i + 1}
              </span>
            </div>
          ))}
          {previews.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="aspect-square border-2 border-dashed border-black/30 flex flex-col items-center justify-center hover:bg-white/60"
            >
              <Camera className="w-6 h-6 text-zinc-400" />
              <span className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Add
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

async function streamNotes({ endpoint, body, isFormData = false, onStatus, onDelta, onProgress, onDone, onError }) {
  const token = localStorage.getItem("access_token");
  const headers = {
    "X-Requested-With": "XMLHttpRequest",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  if (!isFormData) headers["Content-Type"] = "application/json";
  const res = await fetch(`${API}${endpoint}`, {
    method: "POST",
    headers,
    credentials: "include",
    body: isFormData ? body : JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Stream request failed");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 2);
      if (!raw.startsWith("data:")) continue;
      const payload = raw.slice(5).trim();
      if (!payload) continue;
      let ev;
      try { ev = JSON.parse(payload); } catch { continue; }
      if (ev.type === "status") onStatus?.(ev.message);
      else if (ev.type === "delta") onDelta?.(ev.text);
      else if (ev.type === "progress") onProgress?.(ev);
      else if (ev.type === "done") { onDone?.(ev.note); return; }
      else if (ev.type === "error") { onError?.(ev.message); return; }
    }
  }
}

export default function Generate() {
  const [tab, setTab] = useState("text");
  const [format, setFormat] = useState("lecture");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [streamText, setStreamText] = useState("");
  const [progress, setProgress] = useState(null); // {stage, done, total}
  const nav = useNavigate();
  const { sub, refresh: refreshSub } = useSubscription();

  // Auto-detect YouTube URLs pasted anywhere and route to the URL tab.
  const YOUTUBE_RE = /^(https?:\/\/)?(www\.|m\.)?(youtube\.com\/(watch\?v=|shorts\/|embed\/|live\/|v\/)|youtu\.be\/)[A-Za-z0-9_-]{6,}([&?#][^\s]*)?$/i;
  const detectYouTube = (raw) => {
    if (!raw) return null;
    const cleaned = raw.trim();
    if (YOUTUBE_RE.test(cleaned)) {
      return cleaned.startsWith("http") ? cleaned : `https://${cleaned}`;
    }
    return null;
  };
  const routeIfYouTube = (raw) => {
    const yt = detectYouTube(raw);
    if (yt) {
      setUrl(yt);
      setTab("url");
      setText("");
      toast.success("YouTube link detected — switched to URL", {
        description: "Press Generate to make notes from the captions.",
      });
      return true;
    }
    return false;
  };

  const streamable = tab === "text" || tab === "url" || tab === "audio";
  const q = sub?.quota;
  const freeTrials = sub?.free_trials_remaining || 0;
  const hasFreeTrial = !sub?.subscribed && freeTrials > 0;
  const blocked = (!sub?.subscribed && freeTrials <= 0) || (q && q.total_available_now <= 0);
  const showLowBanner = q?.low_warning;

  // Handle 402 errors → route to billing
  const handleGenerationError = (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 402) {
      const msg = (detail && typeof detail === "object" && detail.message) || "Subscription required";
      toast.error(msg, {
        action: {
          label: detail?.code === "quota_exhausted" ? "Buy credits" : "Subscribe",
          onClick: () => nav("/app/billing"),
        },
      });
      return true;
    }
    return false;
  };

  const submit = async () => {
    if (blocked) {
      nav("/app/billing");
      return;
    }
    setLoading(true);
    setStreamText("");
    setStatus("");
    setProgress(null);
    try {
      if (tab === "text" || tab === "url") {
        const endpoint = tab === "text" ? "/notes/stream/text" : "/notes/stream/url";
        const body = tab === "text"
          ? { content: text, format, title: title || undefined }
          : { url, format, title: title || undefined };

        if (tab === "text" && text.trim().length < 30) throw new Error("Paste at least 30 characters of content");
        if (tab === "url" && !/^https?:\/\//i.test(url)) throw new Error("Enter a valid http/https URL");

        let noteResult = null;
        let streamError = null;
        await streamNotes({
          endpoint,
          body,
          onStatus: (m) => setStatus(m),
          onDelta: (t) => setStreamText((s) => s + t),
          onDone: (note) => { noteResult = note; },
          onError: (m) => { streamError = m; },
        });
        if (streamError) throw new Error(streamError);
        if (!noteResult) throw new Error("Stream ended without a result");
        refreshSub();
        announceAndNavigate(nav, noteResult);
      } else if (tab === "pdf") {
        if (!pdfFile) throw new Error("Please choose a PDF file");
        const fd = new FormData();
        fd.append("file", pdfFile);
        fd.append("format", format);
        if (title) fd.append("title", title);
        setStatus("Uploading PDF…");
        let noteResult = null, streamError = null;
        await streamNotes({
          endpoint: "/notes/stream/pdf",
          body: fd,
          isFormData: true,
          onStatus: (m) => setStatus(m),
          onDone: (note) => { noteResult = note; },
          onError: (m) => { streamError = m; },
        });
        if (streamError) throw new Error(streamError);
        if (!noteResult) throw new Error("Stream ended without a result");
        refreshSub();
        announceAndNavigate(nav, noteResult);
      } else if (tab === "photos") {
        if (!photoFiles.length) throw new Error("Please add at least 1 photo");
        if (photoFiles.length > 10) throw new Error("Maximum 10 photos per note");
        const fd = new FormData();
        photoFiles.forEach((f) => fd.append("files", f));
        fd.append("format", format);
        if (title) fd.append("title", title);
        setStatus("Uploading photos…");
        let noteResult = null, streamError = null;
        await streamNotes({
          endpoint: "/notes/stream/photos",
          body: fd,
          isFormData: true,
          onStatus: (m) => setStatus(m),
          onDone: (note) => { noteResult = note; },
          onError: (m) => { streamError = m; },
        });
        if (streamError) throw new Error(streamError);
        if (!noteResult) throw new Error("Stream ended without a result");
        refreshSub();
        announceAndNavigate(nav, noteResult);
      } else if (tab === "audio") {
        if (!audioFile) throw new Error("Please choose an audio/video file");
        const fd = new FormData();
        fd.append("file", audioFile);
        fd.append("format", format);
        if (title) fd.append("title", title);
        setStatus("Uploading…");

        // 1) Upload — returns task_id fast (no long-lived SSE for the whole pipeline)
        const startResp = await api.post("/notes/audio/start", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10 * 60 * 1000, // 10 min max just for the upload
        });
        const taskId = startResp?.data?.task_id;
        if (!taskId) throw new Error("Server did not return a task id");

        // 2) Poll for progress
        setStatus("Queued…");
        let lastMessage = "Queued";
        while (true) {
          await new Promise((r) => setTimeout(r, 1500));
          let job;
          try {
            const res = await api.get(`/notes/audio/job/${taskId}`);
            job = res.data;
          } catch (pollErr) {
            // Transient network hiccup — retry silently for a few attempts
            continue;
          }
          if (job.message && job.message !== lastMessage) {
            lastMessage = job.message;
            setStatus(job.message);
          }
          if (job.progress && job.progress.total > 0) {
            setProgress({ stage: job.progress.stage, done: job.progress.done, total: job.progress.total });
          }
          if (job.status === "done" && job.result) {
            refreshSub();
            announceAndNavigate(nav, job.result);
            return;
          }
          if (job.status === "error") {
            throw new Error(job.error || "Audio processing failed");
          }
        }
      }
    } catch (e) {
      if (handleGenerationError(e)) {
        // 402 handled with a "Subscribe / Buy credits" toast action
      } else {
        const msg = e?.response ? formatApiError(e) : e.message;
        toast.error(msg);
      }
      refreshSub();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F9FF]">
      <Navbar />

      <main className="max-w-5xl mx-auto px-6 py-12">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">/ New note</p>
        <h1 className="mt-2 font-heading font-black text-4xl sm:text-6xl tracking-tighter">
          Feed the engine.
        </h1>
        <p className="mt-3 text-zinc-600 max-w-xl">
          Choose an input, pick a format, and let Smart S Notes strip everything unnecessary.
          Every input streams live — audio & video show real-time transcription progress.
        </p>

        {blocked && (
          <div data-testid="gate-banner" className="mt-6 border-2 border-black bg-[#DBEAFE] p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-heading font-bold">
                {sub?.subscribed ? "You've used all videos this cycle." : "Subscribe to start generating notes."}
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-700">
                {sub?.subscribed
                  ? "Buy extra credits to keep going — ₹24 per video."
                  : "Standard plan · ₹699 / month · 33 videos included · 53% OFF."}
              </p>
            </div>
            <button
              data-testid="gate-cta"
              onClick={() => nav("/app/billing")}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-3 bg-[#3B82F6] text-white btn-brutal"
            >
              {sub?.subscribed ? "Buy credits" : "Subscribe now"} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {hasFreeTrial && (
          <div data-testid="trial-banner" className="mt-6 border-2 border-black bg-white p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-heading font-bold">
                🎉 You have <span className="text-[#3B82F6]">1 free note</span> to try Smart S Notes.
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-700">
                Uses GPT-5.2 · exports · sharing — everything included. Subscribe any time for 33 notes/month.
              </p>
            </div>
            <button
              onClick={() => nav("/app/billing")}
              className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 bg-white border-2 border-black hover:bg-[#DBEAFE] transition-colors"
            >
              See plan
            </button>
          </div>
        )}

        {!blocked && showLowBanner && (
          <div data-testid="low-banner" className="mt-6 border-2 border-amber-400 bg-amber-50 p-4 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-amber-900">
              You have only <b>{q.included_remaining} videos</b> remaining in your Standard Plan.
            </p>
            <button
              onClick={() => nav("/app/billing")}
              className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 bg-amber-500 text-white btn-brutal"
            >
              Buy extra credits
            </button>
          </div>
        )}

        <div data-testid="input-tabs" className="mt-10 flex flex-wrap gap-2">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              disabled={loading}
              className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-3 border-2 border-black transition-colors ${
                tab === id ? "bg-black text-white" : "bg-white hover:bg-black/5"
              } disabled:opacity-60`}
            >
              <Icon className="w-4 h-4" /> {label}
              {(id === "text" || id === "url" || id === "audio") && (
                <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#3B82F6] text-white text-[9px]">
                  <Zap className="w-2.5 h-2.5" /> LIVE
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Output format:</span>
          <div className="inline-flex border-2 border-black">
            {["lecture", "meeting"].map((f) => (
              <button
                key={f}
                data-testid={`format-${f}`}
                onClick={() => setFormat(f)}
                disabled={loading}
                className={`px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                  format === f ? "bg-[#3B82F6] text-white" : "hover:bg-black/5"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Title <span className="text-zinc-400">(optional)</span></label>
          <input
            data-testid="input-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Auto-detected from content if empty"
            className="w-full mt-2 px-4 py-3 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2"
          />
        </div>

        <div className="mt-8">
          {tab === "text" && (
            <textarea
              data-testid="input-text"
              value={text}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData("text") || "";
                if (routeIfYouTube(pasted)) e.preventDefault();
              }}
              onChange={(e) => {
                if (routeIfYouTube(e.target.value)) return;
                setText(e.target.value);
              }}
              rows={12}
              placeholder="Paste a lecture transcript, meeting notes, article, or a YouTube link…"
              className="w-full px-4 py-3 bg-white border-2 border-black font-body focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 resize-y"
            />
          )}
          {tab === "url" && (
            <div className="space-y-1.5">
              <input
                data-testid="input-url"
                value={url}
                onPaste={(e) => {
                  const pasted = e.clipboardData?.getData("text") || "";
                  const yt = detectYouTube(pasted);
                  if (yt) {
                    e.preventDefault();
                    setUrl(yt);
                    toast.success("YouTube link ready", { description: "Press Generate to make notes." });
                  }
                }}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/article  •  or a YouTube video URL"
                className="w-full px-4 py-4 bg-white border-2 border-black font-mono focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2"
              />
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Web articles, blog posts, or YouTube videos (uses captions — must have subtitles enabled).
              </p>
            </div>
          )}
          {tab === "pdf" && (
            <UploadZone
              testId="pdf-drop"
              accept="application/pdf,.pdf"
              onFile={setPdfFile}
              label="Drop a PDF"
              hint="Max 20MB · Textbook chapters, papers, slides"
            />
          )}
          {tab === "audio" && (
            <UploadZone
              testId="audio-drop"
              accept=".mp3,.wav,.m4a,.mp4,.webm,.mpeg,.mpga,.mov,.mkv,.aac,.ogg,.flac,audio/*,video/*"
              onFile={setAudioFile}
              label="Drop an audio or video file"
              hint="Up to ~5 hours · MP3, WAV, M4A, MP4, WebM, MOV — we compress & chunk automatically"
            />
          )}
          {tab === "photos" && (
            <PhotosPicker files={photoFiles} setFiles={setPhotoFiles} />
          )}
        </div>

        {(loading || streamText || progress) && (
          <div data-testid="stream-panel" className="mt-8 border-2 border-black bg-white">
            <div className="flex items-center justify-between px-4 py-2 bg-black text-white">
              <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest">
                <span className="w-2 h-2 rounded-full bg-[#3B82F6] animate-pulse" />
                {tab === "audio"
                  ? "Whisper + GPT-5.2"
                  : streamable
                  ? "GPT-5.2 · streaming"
                  : "Processing"}
              </span>
              <span data-testid="stream-status" className="font-mono text-[11px] text-zinc-400">
                {status || (loading ? "…" : "done")}
              </span>
            </div>
            {progress && progress.total > 0 && (
              <div data-testid="progress-bar" className="px-4 pt-3">
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                  <span>{progress.stage === "transcribe" ? "Transcribing chunks" : "Condensing passes"}</span>
                  <span>{progress.done} / {progress.total}</span>
                </div>
                <div className="h-2 bg-zinc-100 border border-black overflow-hidden">
                  <div
                    className="h-full bg-[#3B82F6] transition-all duration-300"
                    style={{ width: `${Math.min(100, (progress.done / progress.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}
            {tab !== "audio" && (
              <div
                data-testid="stream-text"
                className="p-4 max-h-72 overflow-y-auto font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-zinc-800"
              >
                {streamText || <span className="text-zinc-400">Waiting for tokens…</span>}
                {loading && <span className="inline-block w-1.5 h-3.5 bg-[#3B82F6] animate-pulse ml-0.5 align-middle" />}
              </div>
            )}
            {tab === "audio" && (
              <div className="p-4 font-mono text-[11px] text-zinc-600">
                {status || "Preparing…"}
              </div>
            )}
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            {loading ? "Working…" : streamable ? "Streams live · redirects when done" : "Ready when you are."}
          </p>
          <button
            data-testid="generate-btn"
            onClick={blocked ? () => nav("/app/billing") : submit}
            disabled={loading}
            title={blocked ? (sub?.subscribed ? "Buy credits to continue" : "Subscribe to continue") : ""}
            className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest px-6 py-4 bg-[#3B82F6] text-white btn-brutal disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating</>
            ) : blocked ? (
              <>{sub?.subscribed ? "Buy credits" : "Subscribe"} <ArrowRight className="w-4 h-4" /></>
            ) : (
              <>Generate notes <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}
